// VendAgain — create / edit an "In Search Of" wish. Doubles as a saved
// search: matching for-sale listings ping the owner via in-app notification.
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, DollarSign, Compass } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, SECTIONS, formatPrice } from "./shared";

const EMPTY = {
  category: "",
  title: "",
  description: "",
  city: "",
  max_price_cents: null,
  is_active: true,
};

export default function VendAgainIsoNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(EMPTY);
  const [priceInput, setPriceInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!editing) return;
    let alive = true;
    api.get(`/vendagain/iso/${id}`).then(({ data }) => {
      if (!alive) return;
      if (!data.is_owner) {
        toast.error("You can only edit your own alerts.");
        navigate("/vendagain/iso/mine");
        return;
      }
      setForm({
        category: data.category,
        title: data.title,
        description: data.description,
        city: data.city,
        max_price_cents: data.max_price_cents,
        is_active: data.is_active,
      });
      setPriceInput(data.max_price_cents ? (data.max_price_cents / 100).toString() : "");
      setLoaded(true);
    }).catch(() => {
      toast.error("Alert not found");
      navigate("/vendagain/iso/mine");
    });
    return () => { alive = false; };
  }, [id, editing, navigate]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPriceChange = (raw) => {
    setPriceInput(raw);
    if (raw === "") { patch("max_price_cents", null); return; }
    const n = parseFloat(raw);
    patch("max_price_cents", isNaN(n) || n < 0 ? null : Math.round(n * 100));
  };

  const submit = async () => {
    if (!form.category) return toast.error("Pick a category");
    if (form.title.trim().length < 3) return toast.error("Title too short");
    if (form.description.trim().length < 10) return toast.error("Description too short (10+ chars)");
    if (form.city.trim().length < 2) return toast.error("Add a city — we use it to match nearby listings");
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/vendagain/iso/${id}`, form);
        toast.success("Alert updated.");
        navigate("/vendagain/iso/mine");
      } else {
        await api.post("/vendagain/iso", form);
        toast.success("Alert saved — we'll ping you when a match drops.");
        navigate("/vendagain/iso/mine");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-14 text-center">
        <h1 className="font-serif text-3xl">Sign in to save a wish</h1>
        <Link to="/register" className="btn-primary rounded-full inline-flex mt-6 px-6 py-3 font-bold">
          Get started free
        </Link>
      </div>
    );
  }
  if (!loaded) return <div className="mx-auto max-w-3xl px-6 py-14 opacity-60">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 py-14" data-testid="iso-form-page">
      <Link to="/vendagain/iso" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft size={14} /> Back to In Search Of
      </Link>
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mt-4"
            style={{ background: "rgba(111,125,85,0.14)", color: "var(--moss)" }}>
        <Compass className="h-3 w-3" /> {editing ? "Edit alert" : "New alert"}
      </div>
      <h1 className="font-serif text-4xl mt-3">
        {editing ? "Update your wish" : "Post what you're looking for"}
      </h1>
      <p className="mt-2 opacity-70">
        We&rsquo;ll notify you the moment a matching listing drops — same category, same city,
        within your budget. Sellers browsing here can also reach out directly.
      </p>

      <div className="mt-10 space-y-8">
        <div>
          <div className="overline mb-2">Category</div>
          {SECTIONS.map((s) => (
            <div key={s.key} className="mb-4">
              <div className="text-xs uppercase tracking-widest opacity-60 mb-2">{s.label}</div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.filter((c) => c.section === s.key).map((c) => {
                  const active = form.category === c.value;
                  const Icon = c.icon;
                  return (
                    <button key={c.value} type="button"
                            onClick={() => patch("category", c.value)}
                            data-testid={`iso-form-cat-${c.value}`}
                            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition ${active ? "ring-2 ring-offset-1" : ""}`}
                            style={{ background: c.color, color: c.ink }}>
                      <Icon size={12} /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className="overline mb-2">Title</div>
          <Input value={form.title}
                  onChange={(e) => patch("title", e.target.value)}
                  placeholder="e.g. Looking for a used food trailer, 6x12"
                  maxLength={140}
                  data-testid="iso-form-title" />
        </div>

        <div>
          <div className="overline mb-2">Description</div>
          <Textarea rows={5} value={form.description}
                    onChange={(e) => patch("description", e.target.value)}
                    placeholder="Details about what you need — size, features, budget flexibility, pickup preference."
                    maxLength={1000}
                    data-testid="iso-form-desc" />
          <div className="text-[11px] text-right opacity-50 mt-1">{form.description.length}/1000</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <div className="overline mb-2">City</div>
            <Input value={form.city}
                    onChange={(e) => patch("city", e.target.value)}
                    placeholder="Only matches from this city"
                    maxLength={80}
                    data-testid="iso-form-city" />
          </div>
          <div>
            <div className="overline mb-2">Max budget (optional)</div>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
              <Input type="number" min="0" step="0.01"
                      value={priceInput}
                      onChange={(e) => onPriceChange(e.target.value)}
                      placeholder="No limit"
                      className="pl-9"
                      data-testid="iso-form-price" />
            </div>
            {form.max_price_cents ? (
              <div className="mt-2 text-xs opacity-60">Only ping me for listings up to {formatPrice(form.max_price_cents, false)}</div>
            ) : (
              <div className="mt-2 text-xs opacity-60">Leave blank to see every match</div>
            )}
          </div>
        </div>

        {editing && (
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_active}
                    onChange={(e) => patch("is_active", e.target.checked)}
                    data-testid="iso-form-active"
                    className="h-4 w-4 accent-[color:var(--avt-purple)]" />
            Alert active
          </label>
        )}

        <div className="flex gap-3">
          <Button onClick={submit} disabled={busy}
                  className="btn-primary rounded-full inline-flex items-center gap-2 flex-1"
                  data-testid="iso-form-submit">
            {editing ? "Save changes" : "Save alert"}
          </Button>
          <Link to="/vendagain/iso" className="inline-flex">
            <Button variant="outline" className="rounded-full">Cancel</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
